import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, ArrowRightLeft, Users } from "lucide-react";

function useCountdown(expiresAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return { label: "" };
  const target = new Date(expiresAt).getTime();
  const diff = Math.max(0, target - now);
  return {
    label: `${String(Math.floor(diff/3600000)).padStart(2,"0")}:${String(Math.floor((diff%3600000)/60000)).padStart(2,"0")}:${String(Math.floor((diff%60000)/1000)).padStart(2,"0")}`,
  };
}

function MatchCard({ m, onOpen }) {
  const { label } = useCountdown(m.expires_at);
  const partner = m.partner;
  return (
    <div className="rounded-3xl bg-card border border-border p-5 space-y-3 animate-fade-up" data-testid={`match-card-${m.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">
          {m.match_type === "exchange" ? "Language exchange" : "Same language"}
        </p>
        <p className="text-[11px] tracking-wide text-muted-foreground tabular-nums" data-testid={`match-timer-${m.id}`}>{label} left</p>
      </div>
      <p className="font-serif text-3xl leading-tight" data-testid={`partner-name-${m.id}`}>{partner?.name}</p>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {m.match_type === "exchange" ? (
          <>
            <span className="text-secondary">{partner?.native_language}</span>
            <ArrowRightLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span className="text-secondary">{partner?.learning_language}</span>
          </>
        ) : (
          <span className="text-secondary">{partner?.native_language}</span>
        )}
      </div>
      {partner?.bio && (
        <p className="font-serif italic text-foreground/80 border-l-2 border-primary/40 pl-3 text-sm" data-testid={`partner-bio-${m.id}`}>
          "{partner.bio}"
        </p>
      )}
      <Button
        onClick={() => onOpen(m.id)}
        className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
        data-testid={`open-chat-${m.id}`}
      >
        Open the conversation
      </Button>
    </div>
  );
}

export default function Today() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true });

  const load = async () => {
    try {
      const { data } = await api.get("/match/today");
      setState({ loading: false, ...data });
    } catch {
      setState({ loading: false, status: "error" });
    }
  };

  useEffect(() => { load(); }, []);

  if (state.loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const matches = state.matches || (state.match ? [state.match] : []);
  const canRequest = state.can_request_more;
  const limit = state.limit || 1;
  const isPremium = user?.is_premium;

  return (
    <div className="flex-1 flex flex-col p-6 grain">
      <header className="pt-8 flex items-baseline justify-between">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Today</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Hi, {user?.name?.split(" ")[0]}
            {isPremium && (
              <span className="ml-2 text-[10px] tracking-widest uppercase text-primary" data-testid="premium-badge">
                Premium
              </span>
            )}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="rounded-full text-xs tracking-wide uppercase" data-testid="refresh-match">
          Refresh
        </Button>
      </header>

      <section className="mt-8 space-y-4 flex-1">
        {matches.length === 0 && (
          <div className="pt-6 animate-fade-up">
            <p className="text-xs tracking-[0.35em] uppercase text-muted-foreground">Still searching</p>
            <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight">
              No exchange partner yet for<br />
              <span className="italic text-primary">
                {user?.native_language} ↔ {user?.learning_language}
              </span>.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-sm">
              We only match one exchange per day. If nobody matches your pair, we'll keep looking. Check back in a little while.
            </p>
          </div>
        )}

        {matches.map((m) => (
          <MatchCard key={m.id} m={m} onOpen={(id) => nav(`/chat/${id}`)} />
        ))}

        {matches.length > 0 && canRequest && (
          <button
            onClick={load}
            className="w-full rounded-3xl border border-dashed border-border/70 py-6 text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
            data-testid="find-another"
          >
            {isPremium ? "Look for another exchange partner" : "Look for one more today"}
          </button>
        )}

        {!isPremium && matches.length >= limit && (
          <Link to="/premium" className="block" data-testid="upsell-premium-card">
            <div className="rounded-3xl border border-primary/40 bg-primary/5 p-5 space-y-2 hover:bg-primary/10 transition-colors">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                <p className="text-[11px] tracking-[0.3em] uppercase">Premium</p>
              </div>
              <p className="font-serif text-2xl leading-tight">Meet two people a day.</p>
              <p className="text-sm text-muted-foreground">
                Unlock a second daily exchange partner and an in-chat translation helper.
              </p>
              <p className="text-primary text-sm font-medium">Upgrade for $4.99 / month →</p>
            </div>
          </Link>
        )}
      </section>

      <section className="pt-8 pb-4 space-y-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5" strokeWidth={1.5} /> Be kind. It's a real person.</div>
      </section>
    </div>
  );
}
